// * -------------------------------- NPM --------------------------------------
import * as React from 'react'
import { useState } from 'react'

// * -------------------------------- MODULE --------------------------------------
import Button from '../MVButtons/Button'
import FilterInput from '../MVFilters/FilterInput'
import Flex, { AlignItems, Fit, JustifyContent } from '../MVFlex/Flex'
import SearchFilter, { ISearchFilter } from '../MVFilters/SearchFilter'
import TreeFilter, { TreeComponents, TreeFilterProps } from './TreeFilter'
import {
  IDateComponent,
  IMultiSelectComponent,
  ISelectComponent,
  ISingleDateComponent,
  ISingleSelectComponent,
} from '../MVInput/types'
import { IFlexItem } from '../MVFlex/FlexItem'
import { TreeMultiSelectProps } from '../MVTree/Tree'
import { getUniqueId } from '../../../mvfunctions/helpers/stringHelper'
import { useComponentsTranslation } from '../../../services/translation'

type InputComponents = (ISearchFilter | ISelectComponent | IDateComponent) & IFlexItem

export type Components = InputComponents | TreeComponents

export interface FiltersProps {
  fields: Array<Components | null>
  advancedFilters?: {
    label: string
    onClick: () => void
  }
}

const Filters: React.FC<FiltersProps> = props => {
  // * ----------------------------------------------------------------------------------------
  // * -------------------------------- HOOKs --------------------------------------
  // * ----------------------------------------------------------------------------------------
  const { t } = useComponentsTranslation()
  const base = 'components.filters'

  // * ----------------------------------------------------------------------------------------
  // * -------------------------------- INIT --------------------------------------
  // * ----------------------------------------------------------------------------------------
  const { fields, advancedFilters } = props
  const [ids] = useState(fields.map(f => getUniqueId(f?.id)))

  // * ----------------------------------------------------------------------------------------
  // * -------------------------------- RENDERs --------------------------------------
  // * ----------------------------------------------------------------------------------------
  const renderSearchInput = (component: ISearchFilter & IFlexItem) => {
    return (
      <SearchFilter
        {...component}
        kind={'input'}
        initialValue={component.initialValue}
        id={component.id}
        label={component.label}
        savedSearchString={(component.initialValue && (component.initialValue as string)) || undefined} // TODO
        throttle={component.throttle}
        clearable={component.clearable}
        onChange={component.onChange}
        key={component.id}
        disabled={component.disabled}
        grow={component.grow || 0}
        basis={component.basis || 25}
        shrink={component.shrink || 1}
      />
    )
  }

  const renderGenericInputComponent = (component: InputComponents) => {
    return <FilterInput {...component} key={component.id} />
  }

  const renderFilter = (component: Components) => {
    if (component.kind === 'tree') {
      return <TreeFilter key={component.id} {...component} translation={{ t, base }} />
    }

    if (component.kind === 'input') {
      return renderSearchInput(component as ISearchFilter & IFlexItem)
    }

    return renderGenericInputComponent(component)
  }
  const fieldRendered = fields.map((field, index) =>
    field
      ? renderFilter({
        ...field,
        id: ids[index],
        grow: field.grow || 0,
        basis: field.basis || 22,
        shrink: field.shrink || 1,
      })
      : false,
  )

  return (
    <Flex
      alignItems={AlignItems.end}
      fit={Fit.multiLine}
      justifyContent={JustifyContent.start}
      className={'mv-filters'}
      style={{ width: '100%' }}
      spaceSize={'lg'}>
      <>
        {fieldRendered}
        {advancedFilters && (
          <Flex alignItems={AlignItems.center}>
            <Button semantic={'secondary'} label={advancedFilters.label} onClick={() => advancedFilters.onClick()} />
          </Flex>
        )}
      </>
    </Flex>
  )
}

Filters.defaultProps = {}

// * Minimals interfaces of input exposed to create filters when values and changes are managed inside other components (for example filters in table)
export type SearchType = Pick<ISearchFilter, 'label' | 'placeholder'> & {
  kind: 'search'
}

export type SelectType = Pick<ISingleSelectComponent, 'label' | 'options'> & {
  kind: 'select'
}

export type MultiSelectType = Pick<IMultiSelectComponent, 'label' | 'options'> & {
  kind: 'multiSelect'
}

export type DateType = Pick<ISingleDateComponent, 'label' | 'max' | 'min' | 'initialValue'> & {
  kind: 'date'
}

export type MultiTreeType = Pick<TreeFilterProps, 'label' | 'buttonLabel' | 'buttonIcon' | 'placeholder'> &
  Pick<TreeMultiSelectProps, 'data' | 'flow'> & {
  kind: 'tree'
}

export type FilterType = (SelectType | SearchType | DateType | MultiSelectType | MultiTreeType) & {
  filterId: string
}

export default Filters
